/** Extended model metadata (models table / llms.csv). */
export type LlmExtendedFields = {
  cached_input_price_per_million?: number;
  currency?: string;
  model_score?: number;
  routing_band?: number;
  model_type?: string;
  input_modalities?: string;
  output_modalities?: string;
  context_window_tokens?: number;
  max_output_tokens?: number;
  supports_tools?: boolean;
  supports_json?: boolean;
  supports_streaming?: boolean;
  supports_reasoning?: boolean;
  supports_native_files?: boolean;
  approved_for_sensitive_data?: boolean;
  model_status?: string;
  health_status?: string;
};

/** Raw LLM model shape from API or login configuration. */
export type LlmModelLike = LlmExtendedFields & {
  id?: number;
  name?: string;
  provider_id?: number;
  proverID?: number;
  provider_name?: string;
  description?: string;
  input_price_per_million?: number;
  output_price_per_million?: number;
  input_token_pricing?: number;
  output_token_pricing?: number;
};

/** Same thresholds as models.routing_band GENERATED column in MySQL. */
export function routingBandFromModelScore(score: number | undefined | null): number | undefined {
  if (score === undefined || score === null || Number.isNaN(Number(score))) {
    return undefined;
  }
  const s = Number(score);
  if (s < 0.2) {
    return 1;
  }
  if (s < 0.4) {
    return 2;
  }
  if (s < 0.6) {
    return 3;
  }
  if (s < 0.8) {
    return 4;
  }
  return 5;
}

export function defaultLlmExtendedFields(): Required<
  Pick<
    LlmExtendedFields,
    | 'currency'
    | 'model_type'
    | 'input_modalities'
    | 'output_modalities'
    | 'supports_tools'
    | 'supports_json'
    | 'supports_streaming'
    | 'supports_reasoning'
    | 'supports_native_files'
    | 'approved_for_sensitive_data'
    | 'model_status'
    | 'health_status'
  >
> &
  LlmExtendedFields {
  return {
    currency: 'USD',
    model_type: 'chat',
    input_modalities: 'text',
    output_modalities: 'text',
    supports_tools: true,
    supports_json: true,
    supports_streaming: true,
    supports_reasoning: false,
    supports_native_files: false,
    approved_for_sensitive_data: false,
    model_status: 'testing',
    health_status: 'unknown',
  };
}

export function apiBool(value: unknown): boolean | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }
  if (typeof value === 'boolean') {
    return value;
  }
  if (value === 0 || value === '0' || value === 'false') {
    return false;
  }
  if (value === 1 || value === '1' || value === 'true') {
    return true;
  }
  return Boolean(value);
}

export type NormalizedLlmModel = LlmModelLike & {
  provider_id: number;
  proverID: number;
};

export function modelProviderId(model: LlmModelLike): number {
  return Number(model.provider_id ?? model.proverID ?? 0);
}

export function modelInputPrice(model: LlmModelLike): number | undefined {
  const value = model.input_price_per_million ?? model.input_token_pricing;
  return value === undefined || value === null ? undefined : Number(value);
}

export function modelOutputPrice(model: LlmModelLike): number | undefined {
  const value = model.output_price_per_million ?? model.output_token_pricing;
  return value === undefined || value === null ? undefined : Number(value);
}

export type LlmDetailsFromModel = LlmModelLike & {
  name: string;
  description: string;
  provider_id: number;
  input_price_per_million: number | undefined;
  output_price_per_million: number | undefined;
};

export function llmDetailsFromModel(
  source: LlmModelLike & { id?: number; name?: string },
): LlmDetailsFromModel {
  const providerId = modelProviderId(source);
  return {
    ...defaultLlmExtendedFields(),
    ...source,
    id: source.id,
    provider_id: providerId,
    proverID: providerId,
    name: source.name ?? '',
    description: source.description ?? '',
    input_price_per_million: modelInputPrice(source),
    output_price_per_million: modelOutputPrice(source),
    model_score:
      source.model_score === undefined || source.model_score === null
        ? undefined
        : Number(source.model_score),
    routing_band:
      source.routing_band === undefined || source.routing_band === null
        ? undefined
        : Number(source.routing_band),
    supports_tools: apiBool(source.supports_tools) ?? defaultLlmExtendedFields().supports_tools,
    supports_json: apiBool(source.supports_json) ?? defaultLlmExtendedFields().supports_json,
    supports_streaming:
      apiBool(source.supports_streaming) ?? defaultLlmExtendedFields().supports_streaming,
    supports_reasoning:
      apiBool(source.supports_reasoning) ?? defaultLlmExtendedFields().supports_reasoning,
    supports_native_files:
      apiBool(source.supports_native_files) ?? defaultLlmExtendedFields().supports_native_files,
    approved_for_sensitive_data:
      apiBool(source.approved_for_sensitive_data) ??
      defaultLlmExtendedFields().approved_for_sensitive_data,
  };
}

export type LlmSavePayloadInput = LlmModelLike & {
  name: string;
  description?: string;
  provider_id?: number;
  id?: number;
};

/** Payload for create/update model API (omits empty optional numerics). */
export function buildLlmSavePayload(details: LlmSavePayloadInput): LlmSavePayloadInput {
  const payload: LlmSavePayloadInput = {
    ...details,
    name: details.name,
    description: details.description ?? '',
    provider_id: Number(details.provider_id),
    input_price_per_million: Number(details.input_price_per_million),
    output_price_per_million: Number(details.output_price_per_million),
    currency: details.currency ?? 'USD',
  };
  if (details.id) {
    payload.id = details.id;
  }
  const optionalNums: Array<keyof LlmExtendedFields> = [
    'cached_input_price_per_million',
    'model_score',
    'context_window_tokens',
    'max_output_tokens',
  ];
  optionalNums.forEach((key) => {
    const val = details[key] as number | string | undefined;
    if (val !== undefined && val !== null && String(val) !== '') {
      (payload as LlmExtendedFields)[key] = Number(val) as never;
    } else {
      delete (payload as LlmExtendedFields)[key];
    }
  });
  return payload;
}

/** Align provider and pricing fields for UI and legacy call sites. */
export function normalizeLlmModel<T extends LlmModelLike>(model: T): T & NormalizedLlmModel {
  const providerId = modelProviderId(model);
  const inputPrice = modelInputPrice(model);
  const outputPrice = modelOutputPrice(model);

  return {
    ...model,
    provider_id: providerId,
    proverID: providerId,
    ...(inputPrice !== undefined
      ? { input_price_per_million: inputPrice, input_token_pricing: inputPrice }
      : {}),
    ...(outputPrice !== undefined
      ? { output_price_per_million: outputPrice, output_token_pricing: outputPrice }
      : {}),
  };
}

export function normalizeLlmModels<T extends LlmModelLike>(
  models: T[] | undefined | null,
): Array<T & NormalizedLlmModel> {
  return (models ?? []).map(normalizeLlmModel);
}

export function formatModelPriceUsd(value: number | undefined | null, fallback = '—'): string {
  if (value === undefined || value === null || Number.isNaN(Number(value))) {
    return fallback;
  }
  return `$${Number(value).toFixed(2)}`;
}

export function displayRoutingBand(model: LlmModelLike): number | undefined {
  if (model.routing_band != null) {
    return Number(model.routing_band);
  }
  return routingBandFromModelScore(model.model_score);
}

export const ROUTING_BAND_SCORE_HINT =
  'Band is derived from model score: <0.2→1, <0.4→2, <0.6→3, <0.8→4, else 5';

export function bandPillLabel(band: number): string {
  return `Band ${band}`;
}

export type ModelCapabilityLabel = { key: string; label: string; enabled: boolean };

export function modelCapabilityLabels(model: LlmModelLike): ModelCapabilityLabel[] {
  const flags: Array<{ key: keyof LlmExtendedFields; label: string }> = [
    { key: 'supports_tools', label: 'Tools' },
    { key: 'supports_json', label: 'JSON' },
    { key: 'supports_streaming', label: 'Streaming' },
    { key: 'supports_reasoning', label: 'Reasoning' },
    { key: 'supports_native_files', label: 'Native files' },
    { key: 'approved_for_sensitive_data', label: 'Sensitive data OK' },
  ];
  return flags.map(({ key, label }) => ({
    key,
    label,
    enabled: Boolean(apiBool(model[key])),
  }));
}

export function modelStatusBadgeClass(status: string | undefined): string {
  const s = status ?? 'testing';
  if (s === 'active') {
    return 'text-emerald-700 bg-emerald-50';
  }
  if (s === 'disabled' || s === 'deprecated') {
    return 'text-amber-800 bg-amber-50';
  }
  return 'text-[#6A6A6A] bg-[#F4F4F5]';
}
